<template>
  <div v-if="!pageCategoriesLoaded" class="admin-loading">
    <v-progress-circular :size="70" :width="7" indeterminate></v-progress-circular>
  </div>
  <layout-content-vertical-nav v-else :nav-menu-items="adminMenuItems">
    <slot></slot>

    <!-- Slot: Navbar -->
    <template #navbar="{ isVerticalNavMenuActive, toggleVerticalNavMenuActive }">
      <div class="navbar-content-container" :class="{ 'expanded-search': shallShowFullSearch }">
        <!-- Left Content: Search -->
        <div class="d-flex align-center">
          <v-icon v-if="$vuetify.breakpoint.mdAndDown" class="me-3" @click="toggleVerticalNavMenuActive">
            {{ icons.mdiMenu }}
          </v-icon>
          <!-- <app-bar-search
            :shall-show-full-search.sync="shallShowFullSearch"
            :data="appBarSearchData"
            :filter="searchFilterFunc"
            :search-query.sync="appBarSearchQuery"
            @update:shallShowFullSearch="
              handleShallShowFullSearchUpdate(
                isVerticalNavMenuActive,
                toggleVerticalNavMenuActive
              )
            "
          ></app-bar-search> -->
        </div>

        <!-- Right Content: I18n, Light/Dark, Notification & User Dropdown -->
        <div class="d-flex align-center right-row">
          <app-bar-i18n></app-bar-i18n>
          <app-bar-user-menu></app-bar-user-menu>
        </div>
      </div>
    </template>

    <!-- Slot: Footer -->
    <template #footer>
      <div class="d-flex justify-space-between">
        <span> </span>
        <div class="align-center d-none d-md-flex mr-7">
          <span>
            <template v-if="appVersion">
              Version {{ appVersion }}&nbsp;
            </template>
            &copy; {{ new Date().getFullYear() }} Piiri Kit Oy</span>&nbsp;&nbsp;

        </div>
      </div>
    </template>
  </layout-content-vertical-nav>
</template>

<script>
import LayoutContentVerticalNav from "@/@core/layouts/variants/content/vertical-nav/LayoutContentVerticalNav.vue";
// import navMenuItems from "@/navigation/vertical";
import useMenus from "@core/utils/useMenus";

// App Bar Components
import AppBarSearch from "@core/layouts/components/app-bar/AppBarSearch.vue";
import AppBarUserMenu from "@/components/AppBarUserMenu.vue";
import AppBarI18n from "@core/layouts/components/app-bar/AppBarI18n.vue";

import { mdiMenu, mdiHeartOutline } from "@mdi/js";

import { getVuetify } from "@core/utils";

// Search Data
import appBarSearchData from "@/assets/app-bar-search-data";

import { ref, watch, computed } from "@vue/composition-api";
import useAuth from "@/useAuth";
import useAdminNotifications from "@/@core/utils/useAdminNotifications";

export default {
  components: {
    LayoutContentVerticalNav,

    // App Bar Components
    AppBarSearch,
    AppBarUserMenu,
    AppBarI18n,
  },
  setup() {
    const appVersion = process.env.VUE_APP_VERSION;
    const $vuetify = getVuetify();
    const { user, tenant } = useAuth();
    // Search
    const appBarSearchQuery = ref("");
    const shallShowFullSearch = ref(false);
    const maxItemsInGroup = 5;
    const totalItemsInGroup = ref({
      pages: 0,
      files: 0,
      contacts: 0,
    });
    watch(appBarSearchQuery, () => {
      totalItemsInGroup.value = {
        pages: 0,
        files: 0,
        contacts: 0,
      };
    });

    // NOTE: Update search function according to your usage
    const searchFilterFunc = (item, queryText, itemText) => {
      if (item.header || item.divider) return true;

      const itemGroup = (() => {
        if (item.to !== undefined) return "pages";
        if (item.size !== undefined) return "files";
        if (item.email !== undefined) return "contacts";

        return null;
      })();

      const isMatched =
        itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) >
        -1;

      if (isMatched) {
        if (itemGroup === "pages") totalItemsInGroup.value.pages += 1;
        else if (itemGroup === "files") totalItemsInGroup.value.files += 1;
        else if (itemGroup === "contacts")
          totalItemsInGroup.value.contacts += 1;
      }

      return (
        appBarSearchQuery.value &&
        isMatched &&
        totalItemsInGroup.value[itemGroup] <= maxItemsInGroup
      );
    };

    // ? Handles case where in <lg vertical nav menu is open and search is triggered using hotkey then searchbox is hidden behind vertical nav menu overlaty
    const handleShallShowFullSearchUpdate = (
      isVerticalNavMenuActive,
      toggleVerticalNavMenuActive
    ) => {
      if ($vuetify.breakpoint.mdAndDown && isVerticalNavMenuActive) {
        toggleVerticalNavMenuActive();
      }
    };

    const { newFeedbackCount, refreshNewFeedbackCount } = useAdminNotifications();

    const { getTenantAdminMenuItems, fetchPageCategories, pageCategoriesLoaded } = useMenus();

    const adminMenuItems = computed(() => {
      const menuItems = getTenantAdminMenuItems();


      menuItems.forEach((menu) => {
        if (menu.adminNotificationKey) {
          switch (menu.adminNotificationKey) {
            case "newFeedbackCount":
              if (newFeedbackCount.value) {
                menu.badge = newFeedbackCount.value;
                menu.badgeColor = "error";
              }
              else {
                menu.badge = null;
                menu.badgeColor = null;
              }
              break;
            default:
              break;
          }
        }
      });


      return menuItems;
    });

    fetchPageCategories();
    refreshNewFeedbackCount();

    return {
      adminMenuItems,
      pageCategoriesLoaded,
      handleShallShowFullSearchUpdate,

      // Search
      appBarSearchQuery,
      shallShowFullSearch,
      appBarSearchData,
      searchFilterFunc,
      appVersion,
      user,
      tenant,
      icons: {
        mdiMenu,
        mdiHeartOutline,
      },
    };
  },
};
</script>

<style lang="scss" scoped>
.admin-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
}

.navbar-content-container {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
  position: relative;
}

// ? Handle bg of autocomplete for blured appBar
.v-app-bar.bg-blur {
  .expanded-search {
    ::v-deep .app-bar-autocomplete-box div[role="combobox"] {
      background-color: transparent;
    }

    >.d-flex>button.v-icon {
      display: none;
    }

    // ===

    &>.right-row {
      visibility: hidden;
      opacity: 0;
    }

    ::v-deep .app-bar-search-toggler {
      visibility: hidden;
    }
  }
}
</style>
